<template>
	<div class="settings-wrapper languages">
		<BackToSettings />
		<div class="language-title header-30" v-t="'settings_option_translation'" />
		<div class="settings-detail">
			<FormItem :label="$t('settings_translation_defaultlang_title')">
				<Dropdown
					v-model="defaultLangId"
					:options="langsToDisplay"
					search
					@search="onSearchPreferred"
				/>
			</FormItem>
			<FormItem
				:label="$t('settings_translation_languages_title')"
				:help="$t('settings_translation_languages_explanation')"
			>
				<div class="known-languages">
					<div
						class="known-language-item"
						v-for="lang in otherLangs"
						:key="lang.id"
					>
						{{ lang.label }}
						<CloseIcon type="regular" @click="onRemoveLang(lang.id)" />
					</div>
				</div>
				<Dropdown
					v-model="tempLang"
					:options="otherLangsToDisplay"
					search
					@search="onSearchOther"
					:placeholder="$t('settings_translation_addlanguage')"
				/>
			</FormItem>

			<div id="instantTrans">
				<input
					type="checkbox"
					name="autoTrans"
					id="autoTrans"
					class="css-checkbox"
					v-model="autoTranslate"
					tabindex="0"
				/>
				<label
					for="autoTrans"
					class="css-label header-30 header-18"
					v-t="'settings_translation_autotranslate'"
				/>
				<label
					class="settings-helper"
					v-t="'settings_translation_autotranslate_explanation'"
				/>
				<br /><br />
				<Button
					large
					primary
					:disabled="disabled"
					:loading="isUpdating"
					@click="update"
				>
					{{ $t('common_button_save') }}
				</Button>
			</div>
		</div>
	</div>
</template>

<script>
import store, { getMyUserId, setUser } from '@/store'
import { Button, Dropdown, FormItem } from '@/components/common'
import { CloseIcon } from '@/icons'
import BackToSettings from './BackToSettings'
import { post } from '@/helpers/api'
import { getLanguagesData } from '@/helpers/firebase'
import escapeStringRegexp from 'escape-string-regexp'

export default {
	name: 'Language',
	components: { Button, BackToSettings, Dropdown, FormItem, CloseIcon },
	data() {
		return {
			isUpdating: false,
			defaultLangId: store.language || null,
			otherLangs: [],
			languages: [],
			tempLang: null,
			prefSearchTerm: null,
			otherSearchTerm: null,
			autoTranslate: store.auth.user.autotranslate,
		}
	},
	mounted() {
		// get a list of all possible languages (for the two dropdowns)
		const languages = getLanguagesData()
		this.languages = Object.entries(languages.langs).map(([id, lang]) => {
			const localized = lang.localizednames[store.language]
			const label = localized ? `${lang.name} (${localized})` : lang.name
			return { id, label }
		})

		// get localized labels for any other languages that I understand
		this.otherLangs = store.auth.user.languages
			.filter((lang) => lang !== store.language)
			.map((code) => {
				const lang = languages.langs[code]
				const localized = lang.localizednames[store.language]
				const label = localized ? `${lang.name} (${localized})` : lang.name
				return { id: code, label }
			})
	},
	watch: {
		tempLang(langId) {
			if (!langId) return
			const lang = this.languages.find((l) => l.id === langId)
			this.otherLangs.push(lang)
			this.tempLang = null
		},
	},
	computed: {
		langsToDisplay() {
			if (!this.prefSearchTerm) return this.languages
			const regex = new RegExp(escapeStringRegexp(this.prefSearchTerm), 'i')
			return this.languages.filter((l) => regex.test(l.label))
		},
		otherLangsToDisplay() {
			const selectedIds = this.otherLangs.map((o) => o.id)
			const notSelected = this.languages.filter(
				(l) => !selectedIds.includes(l.id)
			)
			if (!this.otherSearchTerm) return notSelected
			const regex = new RegExp(escapeStringRegexp(this.otherSearchTerm), 'i')
			return notSelected.filter((l) => regex.test(l.label))
		},
		defaultLang() {
			return this.languages.find((l) => l.id === this.defaultLangId) || {}
		},
		disabled() {
			const { user } = store.auth
			if (this.autoTranslate !== user.autotranslate) return false
			if (this.defaultLangId !== store.language) return false
			if (this.otherLangs.length !== user.languages.length) return false
			if (this.otherLangs.some((lang) => !user.languages.includes(lang)))
				return false
			return true
		},
	},
	methods: {
		onRemoveLang(langId) {
			this.otherLangs = this.otherLangs.filter((l) => l.id !== langId)
		},
		async update() {
			const { user } = store.auth

			this.isUpdating = true

			//if the translation flag or the preferred language has changed
			// reset the manually translated message cache
			if (
				user.autotranslate !== this.autoTranslate ||
				this.defaultLang.id !== store.language
			) {
				store.translations.reset()
			}

			try {
				const { data } = await post('/api/v1/user.update', {
					id: getMyUserId(),
					language: this.defaultLang.id,
					autotranslate: this.autoTranslate,
					languages: this.otherLangs.map((lang) => lang.id),
				})
				setUser(data.user)

				store.language = data.user.language

				this.isUpdating = false
				store.auth.setUser(data.user, store.userKey)
				this.$alert.success(this.$t('common_confirmation_updated'))
			} catch (error) {
				console.error(error)
				this.$alert.error(this.$t('common_api_error_save'))
				this.isUpdating = false
			}
		},

		onSearchPreferred(term) {
			this.prefSearchTerm = term || null
		},

		onSearchOther(term) {
			this.otherSearchTerm = term || null
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.settings-wrapper.languages {
	max-height: calc(100% - 60px);
	overflow-y: auto;

	.header-30,
	.header-18 {
		color: var(--neutral-800);
	}
	.language-title {
		margin: 0 auto;
		text-align: center;
	}

	.settings-detail {
		max-width: 600px;
	}

	.known-languages {
		@include flex;
		column-gap: 8px;
		row-gap: 4px;
		flex-wrap: wrap;
		margin-bottom: 8px;
		.known-language-item {
			@include flex(8px);
			padding: 4px 6px;
			background: var(--neutral-100);
			border: 1px solid var(--neutral-400);
			font-size: 14px;
			border-radius: 4px;
			.pronto-icon {
				cursor: pointer;
			}
		}
	}
}
</style>
